/**
 * Dashboard-assistant permission modes (AIR-663, collapsed to two by AIR-687).
 * Client-safe: pure types and parsing, no server imports — shared by the panel
 * UI (Shift-Tab toggle), the chat route (requestContext stamp), and the ops
 * tools.
 *
 * - "ask": propose-then-approve — every write renders an approval card.
 * - "act": writes execute server-side inside the turn without a click, except
 *          operations marked alwaysConfirm (one-time secrets, destructive or
 *          identity-changing ops), which still render a card.
 *
 * The mode is a UX consent setting, never a security boundary: every operation
 * executes through its existing guarded server action, whose own authorization
 * guard is what actually protects the write.
 */
export const DASHBOARD_PERMISSION_MODES = ["ask", "act"] as const;

export type DashboardPermissionMode = (typeof DASHBOARD_PERMISSION_MODES)[number];

export const DEFAULT_DASHBOARD_PERMISSION_MODE: DashboardPermissionMode = "ask";

/**
 * Retired AIR-663 modes, migrated at parse time wherever the old value still
 * lives (localStorage, an in-flight request body from a stale tab): "manual"
 * (read-only) folds into "ask" — the assistant now always may propose; "auto"
 * (card auto-fire in the browser) becomes "act" (server-side execution).
 */
const LEGACY_MODE_MAP: Record<string, DashboardPermissionMode> = {
  manual: "ask",
  auto: "act",
};

/** Fail-closed to "ask": unknown values never grant standing approval. */
export function parseDashboardPermissionMode(value: unknown): DashboardPermissionMode {
  if (DASHBOARD_PERMISSION_MODES.includes(value as DashboardPermissionMode)) {
    return value as DashboardPermissionMode;
  }
  if (typeof value === "string" && value in LEGACY_MODE_MAP) {
    return LEGACY_MODE_MAP[value];
  }
  return DEFAULT_DASHBOARD_PERMISSION_MODE;
}

/** Shift-Tab toggle: ask ⇄ act. */
export function nextDashboardPermissionMode(
  mode: DashboardPermissionMode,
): DashboardPermissionMode {
  return mode === "ask" ? "act" : "ask";
}

export const DASHBOARD_PERMISSION_MODE_META: Record<
  DashboardPermissionMode,
  { label: string; hint: string }
> = {
  ask: { label: "Ask", hint: "Every action shows an approval card before it runs" },
  act: {
    label: "Act",
    hint: "Actions run without asking (secrets and destructive changes still confirm)",
  },
};

/** localStorage key; exported so a cross-tab storage listener can match it. */
export const PERMISSION_MODE_STORAGE_KEY = "dashboard-assistant-permission-mode";

/**
 * Browser persistence, shared by the panel provider (owns the React state)
 * and the chat transport (reads the current mode at send time, keeping the
 * transport a module-level singleton). SSR-safe: defaults before hydration.
 * Legacy stored values ("manual"/"auto") migrate through the parser.
 */
export function loadStoredPermissionMode(): DashboardPermissionMode {
  if (typeof window === "undefined") return DEFAULT_DASHBOARD_PERMISSION_MODE;
  return parseDashboardPermissionMode(window.localStorage.getItem(PERMISSION_MODE_STORAGE_KEY));
}

export function storePermissionMode(mode: DashboardPermissionMode): void {
  window.localStorage.setItem(PERMISSION_MODE_STORAGE_KEY, mode);
}
